import axios from "axios";
import { ProjectMember } from "../types";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8003/api";

// Helper — attach the stored JWT to every request.
// The token is written to localStorage by the auth flow in useAppStore.
function authHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function listMembers(projectId: string): Promise<ProjectMember[]> {
  const res = await axios.get(`${API_BASE}/projects/${projectId}/members`, {
    headers: authHeaders(),
  });
  return res.data.members;
}

export async function inviteMember(
  projectId: string,
  email: string,
  role: string
): Promise<ProjectMember> {
  const res = await axios.post(
    `${API_BASE}/projects/${projectId}/members`,
    { email, role },
    { headers: authHeaders() }
  );
  return res.data.member;
}

export async function updateMemberRole(
  projectId: string,
  memberId: string,
  role: string
): Promise<ProjectMember> {
  const res = await axios.patch(
    `${API_BASE}/projects/${projectId}/members/${memberId}`,
    { role },
    { headers: authHeaders() }
  );
  return res.data.member;
}

export async function removeMember(
  projectId: string,
  memberId: string
): Promise<void> {
  await axios.delete(
    `${API_BASE}/projects/${projectId}/members/${memberId}`,
    { headers: authHeaders() }
  );
}

// leaveProject lets the authenticated user remove themselves.
// Uses the /me endpoint rather than /:memberId so the user doesn't need to
// know their own member record ID.
export async function leaveProject(projectId: string): Promise<void> {
  await axios.delete(`${API_BASE}/projects/${projectId}/members/me`, {
    headers: authHeaders(),
  });
}

export async function resendInvite(
  projectId: string,
  memberId: string
): Promise<void> {
  await axios.post(
    `${API_BASE}/projects/${projectId}/members/${memberId}/resend`,
    {},
    { headers: authHeaders() }
  );
}

export interface InviteDetails {
  projectName: string;
  invitedByName: string;
  role: string;
  expiresAt: string;
  status: string;
  email: string;
}

export async function getInviteDetails(token: string): Promise<InviteDetails> {
  const res = await axios.get(`${API_BASE}/invites/${token}`);
  return res.data;
}

export interface AcceptInviteResult {
  projectId: string;
  role: string;
  // Present only when accepting as a new (just-registered) user.
  token?: string;
  user?: {
    id: string;
    name: string;
    email: string;
  };
}

// acceptInvite is called from AcceptInvitePage.
// If the user is already logged in, pass their JWT via authHeaders (the backend
// matches their email to the invite and grants access directly).
// If they are registering, pass name/email/password in the body instead.
export async function acceptInvite(
  token: string,
  body?: { name?: string; email?: string; password?: string }
): Promise<AcceptInviteResult> {
  const res = await axios.post(
    `${API_BASE}/invites/${token}/accept`,
    body ?? {},
    { headers: authHeaders() }
  );
  return res.data;
}

export async function declineInvite(token: string): Promise<void> {
  await axios.post(`${API_BASE}/invites/${token}/decline`);
}
